import {
    createClient,
    createCameraVideoTrack,
    createMicrophoneAudioTrack,
    createScreenVideoTrack,
    registerExtensions
} from 'agora-rtc-sdk-ng/esm'
import VirtualBackgroundExtension from 'agora-extension-virtual-background'
import AGORA_CONFIG from "../agoraConfig"
import api from './api'

/**
 * Manages an Agora RTC Client instance and its associated media tracks.
 * Decouples SDK logic from React to prevent stale closures and complex dependency arrays.
 */
class MeetingClient {
    constructor ( role = 'main' ) {
        this.role = role
        this.client = createClient( { mode: "rtc", codec: "vp8" } )
        this.videoTrack = null
        this.audioTrack = null
        this.processor = null
        this.config = {
            appId: AGORA_CONFIG.APP_ID || "0add0da6f21f4abfaf8af383fd6c51c6",
        }
    }

    // --- Connection Management ---

    async join ( channel, token, uid, user = {} ) {
        if ( this.client.connectionState === "CONNECTED" ) return uid

        this.client.on( "token-privilege-will-expire", async () => {
            try {
                const { data: { data: { agoraChannelToken, agoraChannelShareToken } } } = await api.onlineMeetings.getMeetingAccessToken( channel, user )
                if ( uid.includes( 'sharing' ) )
                    await this.client.renewToken( agoraChannelShareToken )
                else
                    await this.client.renewToken( agoraChannelToken )
            } catch ( err ) {
                console.error( err )
            }

        } )

        this.client.on( "token-privilege-did-expire", async () => {
            console.error( "Token expired. Reconnecting..." )
            try {
                const { data: { data: { agoraChannelToken, agoraChannelShareToken } } } = await api.onlineMeetings.getMeetingAccessToken( channel, user )
                if ( uid.includes( 'sharing' ) )
                    await this.client.renewToken( agoraChannelShareToken )
                else
                    await this.client.renewToken( agoraChannelToken )
            } catch ( err ) {
                console.error( err )
            }
        } )

        return await this.client.join( this.config.appId, channel, token, uid )
    }

    async leave () {
        this.unpublish()
        this.stopTracks()

        if ( this.client.connectionState !== "DISCONNECTED" ) {
            await this.client.leave()
        }
    }

    // --- Track Management ---

    async createCameraTrack ( cameraId, encoderConfig = "480p_2" ) {
        if ( this.videoTrack ) return this.videoTrack

        this.videoTrack = await createCameraVideoTrack( {
            cameraId,
            encoderConfig
        } )

        // Initialize Virtual Background once per client
        if ( !this.processor ) {
            const extension = new VirtualBackgroundExtension( "virtual-background" )
            registerExtensions( [extension] )
            this.processor = extension.createProcessor()
            await this.processor.init()
            this.processor.setOptions( { type: "blur", blurDegree: 1 } )
            await this.processor.disable()

            this.videoTrack.pipe( this.processor ).pipe( this.videoTrack.processorDestination )
        }
        return this.videoTrack
    }

    async createMicrophoneTrack ( micId ) {
        if ( this.audioTrack ) return this.audioTrack

        this.audioTrack = await createMicrophoneAudioTrack( {
            microphoneId: micId || undefined,
            ANS: true,
            AEC: true,
            encoderConfig: "speech_standard"
        } )
        return this.audioTrack
    }

    async createScreenTracks ( config = { encoderConfig: "720p_2", systemAudio: "include" }, onTrackEnded ) {
        const tracks = await createScreenVideoTrack( {
            optimizationMode: "motion",
            encoderConfig: "720p_2",
            systemAudio: "include",
            selfBrowserSurface: "exclude"
        }, 'auto' )

        if ( Array.isArray( tracks ) ) {
            [this.videoTrack, this.audioTrack] = tracks
        } else {
            this.videoTrack = tracks
            this.audioTrack = null
        }

        if ( this.videoTrack && typeof onTrackEnded === 'function' ) {
            this.videoTrack.on( "track-ended", () => {
                onTrackEnded()
            } )
        }

        return { video: this.videoTrack, audio: this.audioTrack }
    }

    // --- Actions ---

    async publish ( type = 'both' ) {
        const tracksToPublish = []
        if ( ( type === 'both' || type === 'video' ) && this.videoTrack ) tracksToPublish.push( this.videoTrack )
        if ( ( type === 'both' || type === 'audio' ) && this.audioTrack ) tracksToPublish.push( this.audioTrack )

        if ( tracksToPublish.length > 0 ) {
            await this.client.publish( tracksToPublish )
        }
    }

    async unpublish ( type = 'both' ) {
        const tracksToUnpublish = []
        if ( ( type === 'both' || type === 'video' ) && this.videoTrack ) tracksToUnpublish.push( this.videoTrack )
        if ( ( type === 'both' || type === 'audio' ) && this.audioTrack ) tracksToUnpublish.push( this.audioTrack )

        if ( tracksToUnpublish.length > 0 ) {
            await this.client.unpublish( tracksToUnpublish )
        }
    }

    stopVideoTrack () {
        if ( this.videoTrack ) {
            this.videoTrack.stop()
            this.videoTrack.close()
            this.videoTrack = null
        }
    }

    stopAudioTrack () {
        if ( this.audioTrack ) {
            this.audioTrack.stop()
            this.audioTrack.close()
            this.audioTrack = null
        }
    }

    stopTracks () {
        this.stopAudioTrack()
        this.stopVideoTrack()
    }

    // --- Utilities ---

    getSharingSurface () {
        if ( !this.videoTrack ) return null
        try {
            const nativeTrack = this.videoTrack.getMediaStreamTrack()
            return nativeTrack?.getSettings()?.displaySurface || null
        } catch ( err ) {
            return null
        }
    }
}

// Export singletons to be used across the app
export const mainClient = new MeetingClient( 'main' )
export const shareClient = new MeetingClient( 'share' )